// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Three-valued, deliberately.
 *
 * `known-invalid` means "structurally well-formed but published as never-issued" — the SSA's
 * 219-09-9999, or a card number from a vendor's test-card table. Those MUST still be reported:
 * a test card in a training corpus is a data-hygiene problem even though it bills nobody.
 *
 * A two-valued boolean here caused a real bug during the design pass: a `=== true` caller
 * silently dropped every known-invalid plant.
 */
export type ValidateResult = 'valid' | 'known-invalid' | 'invalid';

/**
 * Luhn (mod-10) — the check digit on payment cards.
 *
 * Honest limit, stated in the UI: this rules out a typo, not a coincidence. Roughly one in ten
 * random digit strings of the right length passes, and `0000000000000000` passes.
 */
export function luhn(digits: string): ValidateResult {
  const d = digits.replace(/[^0-9]/g, '');
  if (d.length < 13 || d.length > 19) return 'invalid';
  let sum = 0;
  let alt = false;
  for (let i = d.length - 1; i >= 0; i--) {
    let n = d.charCodeAt(i) - 48;
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  if (sum % 10 !== 0) return 'invalid';
  // Vendor-published test numbers. Reported, but labelled, so a corpus full of Stripe test
  // cards does not read as a corpus full of live cards.
  if (KNOWN_TEST_CARDS.has(d)) return 'known-invalid';
  return 'valid';
}

const KNOWN_TEST_CARDS: ReadonlySet<string> = new Set([
  '4242424242424242',
  '4000056655665556',
  '5555555555554444',
  '5200828282828210',
  '378282246310005',
  '371449635398431',
  '6011111111111117',
  '3056930009020004',
  '4111111111111111',
]);

/** ISO 7064 mod-97-10. The only genuinely strong check in the set. */
export function ibanMod97(iban: string): ValidateResult {
  const s = iban.replace(/[\s-]/g, '').toUpperCase();
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/.test(s)) return 'invalid';
  const expected = IBAN_LENGTHS[s.slice(0, 2)];
  // A country we do not have a length for is still checked by mod-97; we simply cannot
  // reject on length. Each supported country has its own unit test.
  if (expected !== undefined && s.length !== expected) return 'invalid';
  const rearranged = s.slice(4) + s.slice(0, 4);
  let rem = 0;
  for (let i = 0; i < rearranged.length; i++) {
    const c = rearranged.charCodeAt(i);
    // 'A'..'Z' -> 10..35, '0'..'9' -> 0..9
    const chunk = c >= 65 && c <= 90 ? String(c - 55) : String.fromCharCode(c);
    for (let j = 0; j < chunk.length; j++) {
      rem = (rem * 10 + (chunk.charCodeAt(j) - 48)) % 97;
    }
  }
  return rem === 1 ? 'valid' : 'invalid';
}

/** SWIFT IBAN registry lengths. Each entry is covered by a unit test. */
export const IBAN_LENGTHS: Readonly<Record<string, number>> = {
  AD: 24, AE: 23, AL: 28, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BR: 29,
  BY: 28, CH: 21, CR: 22, CY: 28, CZ: 24, DE: 22, DK: 18, DO: 28, EE: 20, EG: 29,
  ES: 24, FI: 18, FO: 18, FR: 27, GB: 22, GE: 22, GI: 23, GL: 18, GR: 27, GT: 28,
  HR: 21, HU: 28, IE: 22, IL: 23, IQ: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20,
  LB: 28, LC: 32, LI: 21, LT: 20, LU: 20, LV: 21, LY: 25, MC: 27, MD: 24, ME: 22,
  MK: 19, MR: 27, MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28, PS: 29, PT: 25,
  QA: 29, RO: 24, RS: 22, RU: 33, SA: 24, SC: 31, SD: 18, SE: 24, SI: 19, SK: 24,
  SM: 27, ST: 25, SV: 28, TL: 23, TN: 24, TR: 26, UA: 29, VA: 22, VG: 24, XK: 20,
};

/**
 * ABA routing transit number: weights 3-7-1 repeating, sum ≡ 0 (mod 10).
 *
 * Honest limit, printed in the dismiss dialog: this accepts ~9.9% of random 9-digit strings
 * (measured over 100,000). Order ids and timestamps do turn up here.
 */
export function abaChecksum(nine: string): ValidateResult {
  const d = nine.replace(/[^0-9]/g, '');
  if (d.length !== 9) return 'invalid';
  const w = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += (d.charCodeAt(i) - 48) * w[i]!;
  if (sum % 10 !== 0) return 'invalid';
  if (d === '000000000') return 'known-invalid';
  return 'valid';
}

/**
 * US SSN structural validity. There is NO checksum — the SSA moved to randomised assignment in
 * June 2011 — so this is exclusion only, and the UI says so. `192-83-7465`, an ordinary order
 * number, matches. That is the honest limit, not a bug.
 */
export function ssnStructural(area: string, group: string, serial: string): ValidateResult {
  if (!/^\d{3}$/.test(area) || !/^\d{2}$/.test(group) || !/^\d{4}$/.test(serial)) return 'invalid';
  const a = Number(area);
  if (a === 0 || a === 666 || a >= 900) return 'invalid';
  if (group === '00') return 'invalid';
  if (serial === '0000') return 'invalid';
  // The SSA's published never-issued advertising number, and the 078-05-1120 wallet-card number.
  if (`${area}-${group}-${serial}` === '219-09-9999') return 'known-invalid';
  if (`${area}-${group}-${serial}` === '078-05-1120') return 'known-invalid';
  return 'valid';
}

/**
 * Does the first segment of a JWT actually base64url-decode to JSON with an `alg`?
 *
 * Implemented against `atob`, NOT Node's `Buffer.from(x, 'base64')`. Buffer is lenient and
 * silently truncates on invalid input; the browser's atob is strict and throws. Testing against
 * Buffer would let `eyJhbGciOiJIUzI1NiJ9!!!` through in Node and fail in a browser.
 */
export function jwtDecodable(token: string): ValidateResult {
  const parts = token.split('.');
  if (parts.length < 2) return 'invalid';
  const head = parts[0]!;
  try {
    let b64 = head.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4 !== 0) b64 += '=';
    const json = atobStrict(b64);
    const obj: unknown = JSON.parse(json);
    if (typeof obj !== 'object' || obj === null) return 'invalid';
    return 'alg' in obj || 'typ' in obj ? 'valid' : 'invalid';
  } catch {
    return 'invalid';
  }
}

/** `atob` in the browser, a strict equivalent under Node (where atob is lenient-ish). */
function atobStrict(b64: string): string {
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(b64)) throw new Error('not base64');
  if (typeof atob === 'function') return atob(b64);
  /* c8 ignore next 2 */
  throw new Error('no base64 decoder');
}

/**
 * AWS access-key-id bodies are widely described as base32 (A–Z, 2–7). That claim is
 * UNVERIFIED, and hard-rejecting `0 1 8 9` would make an id like `AKIA` + `J4XKZ8Q0WVTN9RC2`
 * invisible.
 *
 * Conservative choice: the rule matches `[A-Z0-9]{16}`, and this function is shown in triage
 * as a CONFIDENCE SCORE, never used as a gate. A missed AWS key is unrecoverable; a false
 * positive costs one keystroke.
 */
export function base32Conformant(body: string): boolean {
  return /^[A-Z2-7]+$/.test(body);
}

/** RFC 1918 / loopback / link-local / documentation ranges — never interesting. */
export function isPrivateOrReservedIpv4(ip: string): boolean {
  const parts = ip.split('.');
  if (parts.length !== 4) return true;
  const n: number[] = [];
  for (const p of parts) {
    if (!/^\d{1,3}$/.test(p)) return true;
    const v = Number(p);
    if (v > 255) return true;
    n.push(v);
  }
  const [a, b] = [n[0]!, n[1]!];
  if (a === 10 || a === 127 || a === 0) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  if (a === 169 && b === 254) return true;
  if (a === 100 && b >= 64 && b <= 127) return true;
  if (a >= 224) return true;
  // RFC 5737 documentation ranges.
  if (a === 192 && b === 0 && n[2] === 2) return true;
  if (a === 198 && b === 51 && n[2] === 100) return true;
  if (a === 203 && b === 0 && n[2] === 113) return true;
  return false;
}
